import * as React from 'react'
import { connect } from 'react-redux'

import { offerStyleIsCompactSelector, shouldUseRangedOfferListSelector } from 'embed/selectors'
import { LoanOffer } from 'embed/constants/models'
import { AUTOPAY, DAGGER, EMPTY, LEGAL } from 'embed/utils/offer-display-utils'

import TrackingPixel from 'embed/components/TrackingPixel'

import { LoanStep } from 'even-ts-static'

import { MultiProductState } from 'embed/reducers'

import { LoanOfferProductSubType } from 'embed/constants'
import { DisclaimerModal } from './disclaimer-modal'
import ListItemCompact from './list-item-compact'
import ListItemFull from './list-item-full'
import ListItemFullPreview from './list-item-full-preview'
import ListItemFullRanged from './list-item-full-ranged'
import { PartnerLogo } from './partner-logo'

import { LoanOffersFilterDrawer } from 'embed/experiments'
import { experimentObjectiveComplete } from 'embed/experiments/components/ExperimentActivator/index'
import { uiSelectedLoanStepSelector } from 'embed/selectors/ui'
import { loanRatesLeadUuidSelector, loanRatesUuidSelector } from 'embed/selectors/rates'
import { filtersLoanLenderIdSelector } from 'ui/multi-product/selectors/filters'

export interface ListItemProps {
  bestOfferId?: string
  dispatchOfferCta?: (loan: LoanOffer) => void
  dispatchSeeMoreFromLender?: (offer: LoanOffer) => void
  hasMoreOffers?: boolean
  hideCta?: boolean
  isResultsView?: boolean
  offer: LoanOffer
  tooltipText?: string
  shouldDisplayTransunionDisclaimer: boolean
  shouldUseRangedOfferList?: boolean
  showSeeMoreOffers?: boolean
  viewportHeight?: number
  viewportWidth?: number
}

export interface ListItemStoreProps {
  isFilteredByLender?: boolean
  leadUuid?: string
  offerStyleIsCompact: boolean
  rateTableUuid?: string
  selectedStep: LoanStep
}

type Props = ListItemStoreProps & ListItemProps

const aprSuffix = (offer: LoanOffer) => {
  const key = offer.originator?.key
  const { productSubType } = offer

  if (productSubType === LoanOfferProductSubType.LineOfCredit) {
    return <>&nbsp;{DAGGER}</>
  }

  switch (key) {
    case 'prosper':
      return <>&nbsp;{DAGGER}</>
    case 'light-stream':
      return <>&nbsp;{AUTOPAY}</>
    default:
      return EMPTY
  }
}

const monthlyPaymentSuffix = (offer: LoanOffer) => {
  const key = offer.originator?.key
  const { productSubType } = offer

  if (productSubType === LoanOfferProductSubType.LineOfCredit) {
    return <>&nbsp;{DAGGER}</>
  }

  switch (key) {
    case 'prosper':
      return <>&nbsp;{DAGGER}</>
    case 'light-stream':
      return <>&nbsp;{LEGAL}</>
    default:
      return EMPTY
  }
}

const ListItem: React.FC<Props> = ({
  bestOfferId,
  dispatchOfferCta,
  dispatchSeeMoreFromLender,
  hasMoreOffers,
  hideCta = false,
  isFilteredByLender,
  isResultsView,
  leadUuid,
  offer,
  offerStyleIsCompact,
  rateTableUuid,
  selectedStep,
  shouldDisplayTransunionDisclaimer,
  shouldUseRangedOfferList,
  showSeeMoreOffers,
  tooltipText,
  viewportWidth = 0,
}) => {
  const [el, setEl] = React.useState<HTMLLIElement | undefined>(undefined)

  const handleLoanOfferCtaClick = React.useCallback(
    (event: React.MouseEvent<Element>) => {
      // Prevent multiple windows from opening on offer click due to multiple click targets
      event.stopPropagation()
      dispatchOfferCta?.(offer)
      experimentObjectiveComplete(LoanOffersFilterDrawer)
    },
    [dispatchOfferCta, offer],
  )

  const seeMoreFromLender = React.useCallback(() => {
    dispatchSeeMoreFromLender?.(offer)
  }, [dispatchSeeMoreFromLender, offer])

  const disclaimer = (
    <DisclaimerModal
      el={el}
      hasMoreOffers={hasMoreOffers}
      isFilteredByLender={isFilteredByLender}
      offer={offer}
      offerStyleIsCompact={offerStyleIsCompact}
      seeMoreFromLender={seeMoreFromLender}
      showSeeMoreOffers={showSeeMoreOffers}
    />
  )

  const partnerLogo = (
    <PartnerLogo
      dispatchOfferCta={dispatchOfferCta}
      handleLoanOfferCtaClick={handleLoanOfferCtaClick}
      offer={offer}
      offerStyleIsCompact={offerStyleIsCompact}
    />
  )

  const trackingPixel = isResultsView && (
    <TrackingPixel rateTableUuid={rateTableUuid} leadUuid={leadUuid} offerUuid={offer.uuid} />
  )

  const listItemProps = {
    aprSuffix: aprSuffix(offer),
    bestOfferId,
    disclaimer,
    hideCta,
    isResultsView,
    monthlyPaymentSuffix: monthlyPaymentSuffix(offer),
    offer,
    onLoanOfferCtaClick: handleLoanOfferCtaClick,
    partnerLogo,
    selectedStep,
    setEl,
    shouldDisplayTransunionDisclaimer,
    trackingPixel,
    viewportWidth,
  }

  if (!isResultsView) {
    return offerStyleIsCompact ? (
      <ListItemCompact
        aprSuffix={aprSuffix(offer)}
        disclaimer={disclaimer}
        monthlyPaymentSuffix={monthlyPaymentSuffix(offer)}
        offer={offer}
        onLoanOfferCtaClick={handleLoanOfferCtaClick}
        partnerLogo={partnerLogo}
        selectedStep={selectedStep}
        setEl={setEl}
        shouldDisplayTransunionDisclaimer={shouldDisplayTransunionDisclaimer}
        trackingPixel={trackingPixel}
      />
    ) : shouldUseRangedOfferList ? (
      <ListItemFullPreview {...listItemProps} />
    ) : (
      <ListItemFull {...listItemProps} />
    )
  }

  return offerStyleIsCompact ? (
    <ListItemCompact
      aprSuffix={aprSuffix(offer)}
      disclaimer={disclaimer}
      isResultsView={isResultsView}
      monthlyPaymentSuffix={monthlyPaymentSuffix(offer)}
      offer={offer}
      onLoanOfferCtaClick={handleLoanOfferCtaClick}
      partnerLogo={partnerLogo}
      selectedStep={selectedStep}
      setEl={setEl}
      shouldDisplayTransunionDisclaimer={shouldDisplayTransunionDisclaimer}
      tooltipText={tooltipText}
      trackingPixel={trackingPixel}
    />
  ) : shouldUseRangedOfferList ? (
    <ListItemFullRanged
      aprSuffix={aprSuffix(offer)}
      bestOfferId={bestOfferId}
      disclaimer={disclaimer}
      hideCta={hideCta}
      isResultsView={isResultsView}
      monthlyPaymentSuffix={monthlyPaymentSuffix(offer)}
      offer={offer}
      onLoanOfferCtaClick={handleLoanOfferCtaClick}
      partnerLogo={partnerLogo}
      selectedStep={selectedStep}
      setEl={setEl}
      shouldDisplayTransunionDisclaimer={shouldDisplayTransunionDisclaimer}
      tooltipText={tooltipText}
      trackingPixel={trackingPixel}
      viewportWidth={viewportWidth}
    />
  ) : (
    <ListItemFull
      aprSuffix={aprSuffix(offer)}
      bestOfferId={bestOfferId}
      disclaimer={disclaimer}
      hideCta={hideCta}
      isResultsView={isResultsView}
      monthlyPaymentSuffix={monthlyPaymentSuffix(offer)}
      offer={offer}
      onLoanOfferCtaClick={handleLoanOfferCtaClick}
      partnerLogo={partnerLogo}
      selectedStep={selectedStep}
      setEl={setEl}
      shouldDisplayTransunionDisclaimer={shouldDisplayTransunionDisclaimer}
      tooltipText={tooltipText}
      trackingPixel={trackingPixel}
      viewportWidth={viewportWidth}
    />
  )
}

const mapStateToProps = (state: MultiProductState) => ({
  isFilteredByLender: filtersLoanLenderIdSelector(state),
  leadUuid: loanRatesLeadUuidSelector(state),
  offerStyleIsCompact: offerStyleIsCompactSelector(state),
  rateTableUuid: loanRatesUuidSelector(state),
  selectedStep: uiSelectedLoanStepSelector(state),
  shouldUseRangedOfferList: shouldUseRangedOfferListSelector(state),
})

export default connect<ListItemStoreProps, {}, ListItemProps>(mapStateToProps)(ListItem)
